import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';

import { DeviceUiChannelsDeviceBehaviorPlugin } from '@shure/device-ui/channels/data-access';
import { IntellimixType } from '@shure/device-ui/intellimix/data-access';
import { DeviceUiIntellimixDeviceBehaviorPlugin } from '@shure/device-ui/intellimix/data-access';
import { FactoryResetText } from '@shure/device-ui/settings/factory-reset/data-access';
import { DeviceUiSettingsFactoryResetBehaviorPlugin } from '@shure/device-ui/settings/factory-reset/data-access';
import { DeviceUiSettingsGeneralBehaviorPlugin } from '@shure/device-ui/settings/general/data-access';
import { DspType } from '@shure/device-ui/shared/dsp/data-access';
import { DeviceUiPage, DeviceUiRoute } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import { CoverageMode, DeviceMixerType, DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin, DeviceNameBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import { CoverageImage, CoverageImageProvider } from '@shure/shared/angular/features/device-coverage/ui';
import {
	LightsSettingsDeviceBehaviorPlugin,
	LightsSettingsSupportType
} from '@shure/shared/angular/features/device-lights-settings/data-access';
import {
	LightsImage,
	LightsImageProvider
} from '@shure/shared/angular/features/device-lights-settings/feature-device-image-next';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class Mxa925DeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSettingsFactoryResetBehaviorPlugin,
		DeviceUiIntellimixDeviceBehaviorPlugin,
		LightsSettingsDeviceBehaviorPlugin,
		DeviceUiChannelsDeviceBehaviorPlugin,
		DeviceUiSettingsGeneralBehaviorPlugin,
		DeviceNameBehaviorPlugin,
		CoverageImageProvider,
		LightsImageProvider
{
	public readonly models: DeviceModel[] = [DeviceModel.MXA925R, DeviceModel.MXA925S];

	constructor(protected readonly transloco: TranslocoService) {}

	public getAvailablePages$(
		_model: DeviceModel,
		options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		let pages = [
			{
				route: DeviceUiRoute.Channels,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.channels')
			},
			{
				route: DeviceUiRoute.Intellimix,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.intellimix')
			},
			{
				route: DeviceUiRoute.Presets,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.presets')
			},
			{
				route: DeviceUiRoute.Settings,
				label: this.transloco.translate('scDevUiSharedDevBehavior.toolbar.labels.settings')
			}
		];

		if (options?.autoCoverageMode) {
			pages = pages.filter((p) => p.route !== DeviceUiRoute.Channels);
		}

		return of(pages);
	}

	public getDefaultRoute(_model: DeviceModel, coverageMode?: CoverageMode): DeviceUiRoute | undefined {
		return coverageMode === CoverageMode.Manual ? DeviceUiRoute.Channels : DeviceUiRoute.Intellimix;
	}

	public getResetText(_model: DeviceModel): FactoryResetText {
		return {
			tab$: this.transloco.selectTranslate('scDevUiSharedDevBehavior.settings.factory-reset'),
			title: this.transloco.translate('scDevUiSharedDevBehavior.settings.factory-reset'),
			button: this.transloco.translate(
				'scDevUiSharedDevBehavior.settings.factory-reset-page.restore-factory-defaults'
			)
		};
	}

	public getIntellimixType(_model: DeviceModel): IntellimixType | undefined {
		return IntellimixType.Advanced;
	}

	public revertAutomixerDefaultsCapability(_model: DeviceModel): boolean {
		return true;
	}

	public getCoverageImage(model: DeviceModel): CoverageImage {
		return {
			live: `device-${model.toLowerCase()}-back.svg`,
			virtual: `device-${model.toLowerCase()}-back-line.svg`,
			isImageRound: model === DeviceModel.MXA925R ? true : false
		};
	}

	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.Supported;
	}

	public getLightsImage(model: DeviceModel): LightsImage {
		return {
			imageUrl: `device-mxa9xx-${model === DeviceModel.MXA925S ? 's' : 'r-rotated'}.svg`
		};
	}

	public showPatentInfo(): boolean {
		return true;
	}

	public getMixerType(_model: DeviceModel): DeviceMixerType | undefined {
		return DeviceMixerType.Intellimix;
	}

	public hideAgcMetering(_model: DeviceModel): boolean {
		return true;
	}

	public getIntellimixChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> | undefined {
		return new Map<DspType, number>([
			[DspType.Agc, 1],
			[DspType.Aec, 2],
			[DspType.Nr, 3],
			[DspType.Deverb, 4],
			[DspType.Denoiser, 5],
			[DspType.Peq, 6],
			[DspType.Compressor, 7],
			[DspType.Delay, 8]
		]);
	}

	public getDeviceNameMaxLength(_model: DeviceModel): number | undefined {
		return 31;
	}

	public getChannelsChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> | undefined {
		return new Map<DspType, number>([
			[DspType.Peq, 1],
			[DspType.Eq, 2]
		]);
	}

	public usePostGateValues(): boolean {
		return true;
	}

	public useAIAec(_model: DeviceModel): boolean {
		return true;
	}

	public useAIDenoiser(_model: DeviceModel): boolean {
		return true;
	}

	public useAIDeverb(_model: DeviceModel): boolean {
		return true;
	}

	public useAINoiseReduction(_model: DeviceModel): boolean {
		return true;
	}

	public canResetCoverageAreas(coverageMode: CoverageMode): boolean {
		return coverageMode === CoverageMode.Automatic;
	}

	public supportsChannels(coverageMode: CoverageMode): boolean {
		return coverageMode === CoverageMode.Manual;
	}
}
